import { type ReactNode } from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { type Tone } from "@/lib/status";
import { cn } from "@/lib/cn";

const fillTone: Record<Tone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
  neutral: "bg-fg-muted",
  accent: "bg-accent",
};

export interface ProgressBarProps {
  /** Current value (clamped to [0, max]). */
  value: number;
  /** Maximum value. Defaults to 100. */
  max?: number;
  /** Bar color. Defaults to "accent". */
  tone?: Tone;
  /** Optional label rendered above the bar. */
  label?: ReactNode;
  /** Show "value/max" (or a percentage) text on the right of the label row. */
  showValue?: boolean;
  /** Thinner bar with no label row spacing — for table cells. */
  compact?: boolean;
  className?: string;
}

/**
 * ProgressBar — determinate progress (DESIGN-SYSTEM §5).
 * Replaces all self-made div-bars (warmup, campaign, survival, etc.).
 */
export function ProgressBar({
  value,
  max = 100,
  tone = "accent",
  label,
  showValue = false,
  compact = false,
  className,
}: ProgressBarProps) {
  const safeMax = max > 0 ? max : 100;
  const clamped = Math.min(Math.max(value, 0), safeMax);
  const pct = (clamped / safeMax) * 100;

  return (
    <div className={cn("w-full", className)}>
      {(label || showValue) && (
        <div className="mb-1 flex items-center justify-between gap-2 text-xs text-fg-muted">
          {label && <span className="truncate">{label}</span>}
          {showValue && (
            <span className="font-mono tabular-nums">
              {Math.round(clamped)}/{safeMax}
            </span>
          )}
        </div>
      )}
      <ProgressPrimitive.Root
        value={clamped}
        max={safeMax}
        className={cn(
          "relative w-full overflow-hidden rounded-full bg-bg-elevated",
          compact ? "h-1.5" : "h-2",
        )}
      >
        <ProgressPrimitive.Indicator
          className={cn("h-full rounded-full transition-[width] duration-300", fillTone[tone])}
          style={{ width: `${pct}%` }}
        />
      </ProgressPrimitive.Root>
    </div>
  );
}
ProgressBar.displayName = "ProgressBar";
